import { useState } from 'react';
import { ArrowLeft, Check, Target } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { ProgramDrill } from '@/types/training-program';

type OptionalCategory = 'putting' | 'shortGame' | 'bunker';

interface AddOptionalDrillProps {
  availableDrills: {
    putting: ProgramDrill[];
    shortGame: ProgramDrill[];
    bunker: ProgramDrill[];
  };
  onBack: () => void;
  onAddDrill: (drill: ProgramDrill) => void;
  maxOptionalDrills: number;
  currentOptionalCount: number;
}

const categoryLabels: Record<OptionalCategory, string> = {
  putting: 'Putting',
  shortGame: 'Short Game',
  bunker: 'Bunker',
};

// UX-AUDIT-V3-005: Category emoji icons
const categoryIcons: Record<OptionalCategory, string> = {
  putting: '⛳',
  shortGame: '🏌️',
  bunker: '🏖️',
};

export function AddOptionalDrill({
  availableDrills,
  onBack,
  onAddDrill,
  maxOptionalDrills,
  currentOptionalCount,
}: AddOptionalDrillProps) {
  const [selectedCategory, setSelectedCategory] = useState<OptionalCategory | null>(null);
  const [selectedDrillId, setSelectedDrillId] = useState<string | null>(null);
  const remainingSlots = Math.max(maxOptionalDrills - currentOptionalCount, 0);
  const canAdd = remainingSlots > 0 && selectedCategory && selectedDrillId;

  const drillsForCategory = selectedCategory ? availableDrills[selectedCategory] : [];
  const selectedDrill = drillsForCategory.find((drill) => drill.drill_id === selectedDrillId);

  // UX-AUDIT-V3-203: pb-40 to ensure bottom nav is accessible below sticky CTA
  return (
    <div className="min-h-screen pb-40 bg-white">
      <header className="bg-gradient-to-r from-[#0D382A] to-[#196644] text-white">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center gap-3">
          <button
            onClick={onBack}
            className="h-9 w-9 rounded-full border border-white/30 flex items-center justify-center hover:bg-white/10"
            aria-label="Go back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <p className="text-sm text-white/70">Add Optional Drill</p>
            <h1 className="text-2xl font-semibold">Choose an optional drill</h1>
            {/* UX-AUDIT-V3-006: Full slots counter copy */}
            <p className="text-sm text-white/70 mt-1">You can add up to 2 optional drills. {remainingSlots} {remainingSlots === 1 ? 'slot' : 'slots'} available.</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* UX-AUDIT-V3-007: Why card with blue background */}
        <Card className="bg-blue-50 border-blue-100">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2 text-blue-700">
              <Target className="w-4 h-4" />
              <p className="text-sm font-medium">Why Add Optional Drills?</p>
            </div>
            <p className="text-sm text-gray-600">
              Optional drills help you build a complete game without overwhelming your main focus.
              Choose one or two to balance your training.
            </p>
          </CardContent>
        </Card>

        {!selectedCategory ? (
          <div className="space-y-3">
            {(Object.keys(availableDrills) as OptionalCategory[]).map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className="w-full text-left"
              >
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4 flex items-center gap-3">
                    {/* UX-AUDIT-V3-005: Category emoji icons */}
                    <span className="text-2xl">{categoryIcons[category]}</span>
                    <div>
                      <p className="text-[#0D382A] font-medium">{categoryLabels[category]}</p>
                      <p className="text-sm text-gray-500">
                        {availableDrills[category].length} curated drills
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg text-[#0D382A]">{categoryLabels[selectedCategory]}</h2>
              <Button variant="outline" size="sm" onClick={() => {
                setSelectedCategory(null);
                setSelectedDrillId(null);
              }}>
                Change Category
              </Button>
            </div>

            <div className="space-y-3">
              {drillsForCategory.map((drill) => {
                const isSelected = drill.drill_id === selectedDrillId;
                return (
                  <button
                    key={drill.drill_id}
                    onClick={() => setSelectedDrillId(drill.drill_id)}
                    className="w-full text-left"
                  >
                    <Card className={isSelected ? 'border-[#196644] shadow-[0_2px_12px_rgba(25,102,68,0.12)]' : ''}>
                      <CardContent className="p-4 flex items-center justify-between gap-3">
                        <div>
                          <p className="text-[#0D382A] font-medium">{drill.drill_name}</p>
                          <p className="text-sm text-gray-500">{drill.focus_area}</p>
                        </div>
                        {isSelected && (
                          <div className="h-6 w-6 rounded-full bg-[#196644] text-white flex items-center justify-center">
                            <Check className="w-4 h-4" />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* UX-AUDIT-V3-203: Position sticky CTA above bottom nav */}
      <div className="sticky bottom-16 left-0 right-0 z-40 bg-white border-t border-gray-200 p-4">
        {/* UX-AUDIT-V3-008: Add button includes drill name */}
        <Button
          onClick={() => selectedDrill && onAddDrill(selectedDrill)}
          className="w-full"
          disabled={!canAdd}
        >
          {selectedDrill ? `Add ${selectedDrill.drill_name} to My Program` : 'Add Drill'}
        </Button>
      </div>
    </div>
  );
}
